import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Cookie, X } from "lucide-react";
import { useLocale } from "@/i18n/useLocale";

const KEY = "spiruva_cookie_consent_v1";

/**
 * GDPR-compliant cookie consent banner.
 * Three actions: Accept all, Reject all, Manage (which expands granular toggles).
 * Persists decision in localStorage with timestamp + version. Re-prompts if KEY missing.
 *
 * Hidden on /admin/* routes — admins have already made operational decisions about
 * cookies for the business and the banner overlaps the leads table footer.
 */
export const CookieConsent = () => {
  const { t } = useTranslation();
  const { L } = useLocale();
  const { pathname } = useLocation();
  const [show, show_] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [prefs, setPrefs] = useState({ analytics: true, marketing: false });
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    try {
      const v = localStorage.getItem(KEY);
      if (!v) show_(true);
    } catch { /* SSR / privacy mode */ }
    const tm = setTimeout(() => setMounted(true), 400);
    return () => clearTimeout(tm);
  }, []);

  const persist = (decision) => {
    try {
      localStorage.setItem(KEY, JSON.stringify({
        ...decision,
        version: 1,
        timestamp: new Date().toISOString(),
      }));
    } catch { /* ignore */ }
    show_(false);
  };

  const acceptAll = () => persist({ essential: true, analytics: true, marketing: true });
  const rejectAll = () => persist({ essential: true, analytics: false, marketing: false });
  const acceptSelected = () => persist({ essential: true, ...prefs });

  if (!show) return null;
  if (pathname.startsWith("/admin")) return null;

  return (
    <div
      data-testid="cookie-consent-banner"
      role="dialog"
      aria-label={t("cookies.eyebrow")}
      aria-live="polite"
      className={`fixed left-4 right-4 md:left-6 md:right-6 bottom-4 md:bottom-6 z-40 max-w-3xl md:left-auto md:right-6 transition-all duration-500 ${mounted && show ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6 pointer-events-none"}`}
    >
      <div className="bg-brand-black text-brand-cream border border-brand-gold/30 shadow-[0_22px_60px_-18px_rgba(0,0,0,0.55)] relative overflow-hidden">
        <div className="absolute inset-0 grain opacity-15 mix-blend-overlay" aria-hidden />
        <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-brand-teal via-brand-gold to-brand-green" aria-hidden />

        <div className="relative p-5 md:p-6">
          <div className="flex items-start gap-3">
            <Cookie size={18} className="text-brand-gold shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="eyebrow-light mb-1" data-testid="cookie-eyebrow">◦  {t("cookies.eyebrow")}</p>
              <p className="text-sm text-brand-cream/85 leading-relaxed pr-6" data-testid="cookie-body">
                {t("cookies.body")}{" "}
                <Link to={L("/privacy")} data-testid="cookie-privacy-link" className="underline decoration-brand-gold/50 underline-offset-2 hover:decoration-brand-gold">{t("cookies.body_link")}</Link>.
              </p>
            </div>
            <button
              onClick={rejectAll}
              aria-label={t("cookies.close")}
              data-testid="cookie-close"
              className="text-brand-cream/50 hover:text-brand-cream transition shrink-0"
            >
              <X size={16} />
            </button>
          </div>

          {expanded && (
            <div data-testid="cookie-expanded-prefs" className="mt-5 pt-5 border-t border-brand-cream/15 space-y-3">
              <Toggle
                title={t("cookies.essential_title")}
                desc={t("cookies.essential_desc")}
                checked
                disabled
              />
              <Toggle
                title={t("cookies.analytics_title")}
                desc={t("cookies.analytics_desc")}
                checked={prefs.analytics}
                testid="cookie-toggle-analytics"
                onChange={(v) => setPrefs((p) => ({ ...p, analytics: v }))}
              />
              <Toggle
                title={t("cookies.marketing_title")}
                desc={t("cookies.marketing_desc")}
                checked={prefs.marketing}
                testid="cookie-toggle-marketing"
                onChange={(v) => setPrefs((p) => ({ ...p, marketing: v }))}
              />
            </div>
          )}

          <div className="mt-5 flex flex-wrap gap-2 justify-end">
            {!expanded ? (
              <button
                onClick={() => setExpanded(true)}
                data-testid="cookie-manage"
                className="text-xs uppercase tracking-[0.18em] px-4 py-2.5 text-brand-cream/70 hover:text-brand-cream transition border border-brand-cream/20 hover:border-brand-cream/40"
              >
                {t("cookies.manage")}
              </button>
            ) : (
              <button
                onClick={acceptSelected}
                data-testid="cookie-accept-selected"
                className="text-xs uppercase tracking-[0.18em] px-4 py-2.5 text-brand-cream/85 border border-brand-cream/25 hover:border-brand-gold hover:text-brand-gold transition"
              >
                {t("cookies.save_selection")}
              </button>
            )}
            <button
              onClick={rejectAll}
              data-testid="cookie-reject"
              className="text-xs uppercase tracking-[0.18em] px-4 py-2.5 text-brand-cream/70 hover:text-brand-cream transition border border-brand-cream/20 hover:border-brand-cream/40"
            >
              {t("cookies.reject")}
            </button>
            <button
              onClick={acceptAll}
              data-testid="cookie-accept-all"
              className="text-xs font-medium uppercase tracking-[0.18em] px-5 py-2.5 bg-brand-gold text-brand-black hover:bg-brand-cream transition"
            >
              {t("cookies.accept")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const Toggle = ({ title, desc, checked, onChange, disabled, testid }) => (
  <label className={`flex items-start gap-3 ${disabled ? "opacity-60" : "cursor-pointer"}`}>
    <span
      role="switch"
      aria-checked={checked}
      onClick={() => !disabled && onChange?.(!checked)}
      data-testid={testid}
      className={`relative shrink-0 mt-0.5 w-9 h-5 transition ${checked ? "bg-brand-gold" : "bg-brand-cream/20"}`}
    >
      <span
        className={`absolute top-0.5 ${checked ? "right-0.5" : "left-0.5"} w-4 h-4 bg-brand-cream transition-all duration-200`}
      />
    </span>
    <span className="flex-1">
      <span className="block text-sm text-brand-cream font-medium">{title}</span>
      <span className="block text-xs text-brand-cream/60 mt-0.5 leading-relaxed">{desc}</span>
    </span>
  </label>
);

export default CookieConsent;
